/**
 * The signed-in person's own preferences, reached from the account menu.
 *
 * Each preference is one `SettingRow` in a panel, so adding one is adding a
 * row and not reshaping the page. Locale and time zone start from the tenant's
 * and are stored only when the person picks something else.
 *
 * @module components/settings/UserSettings
 */

import React, { useEffect, useState } from "react";

import { applyTheme, currentTheme } from "@/components/shared/theme";
import type { ThemeName } from "@/components/shared/workspace-types";
import {
  Button,
  Combobox,
  FormField,
  PageHeader,
  Panel,
  SegmentedControl,
  SelectField,
  Text,
} from "@/components/ui";
import { formatDateTime, type Region, type RegionPreference } from "@/utils/format-instant";
import { localeLabel, localeOptions, timeZoneOptions } from "@/utils/region";
import { applyTextSize, isTextSize, type TextSizePreference } from "@/utils/text-size";

/** Stands for "no choice of my own", since a select cannot hold nothing. */
const TENANT_DEFAULT = "tenant";

/** A fixed moment, so the preview shows the format and not the clock. */
const SAMPLE_INSTANT = "2026-10-01T13:05:00Z";

export interface UserSettingsProps {
  /** What the person has saved; absent means the tenant's. */
  preferences: RegionPreference & { textSize?: string | null };
  /** What each falls back to, named in the first option of its picker. */
  tenantDefault: Region;
  pending: boolean;
  /** `null` clears a choice, returning the person to the tenant's. */
  onSave: (next: {
    locale: string | null;
    timeZone: string | null;
    /** `null` is the default size. */
    textSize: TextSizePreference | null;
  }) => void;
}

function SettingRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
      <div className="min-w-0">
        <p className="font-medium">{title}</p>
        <Text tone="muted">{description}</Text>
      </div>
      {children}
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <UserSettings preferences={user} tenantDefault={region} pending={false} onSave={save} />
 * ```
 */
export function UserSettings({
  preferences,
  tenantDefault,
  pending,
  onSave,
}: UserSettingsProps): React.ReactElement {
  const [theme, setTheme] = useState<ThemeName>(currentTheme);
  const [locale, setLocale] = useState(preferences.locale ?? TENANT_DEFAULT);
  const [timeZone, setTimeZone] = useState(preferences.timeZone ?? TENANT_DEFAULT);

  const [textSize, setTextSize] = useState<TextSizePreference>(
    isTextSize(preferences.textSize) ? preferences.textSize : "default",
  );

  // An unsaved preview goes back to the saved size when the page is left.
  const saved = isTextSize(preferences.textSize) ? preferences.textSize : "default";
  useEffect(
    () => () => {
      applyTextSize(saved);
    },
    [saved],
  );

  const chosen: Region = {
    locale: locale === TENANT_DEFAULT ? tenantDefault.locale : locale,
    timeZone: timeZone === TENANT_DEFAULT ? tenantDefault.timeZone : timeZone,
  };
  const changed =
    locale !== (preferences.locale ?? TENANT_DEFAULT) ||
    timeZone !== (preferences.timeZone ?? TENANT_DEFAULT) ||
    textSize !== (isTextSize(preferences.textSize) ? preferences.textSize : "default");

  return (
    <div className="flex flex-col gap-6" data-testid="user-settings">
      <PageHeader
        title="Settings"
        description="Choose how SocratIQ looks and how dates and times appear."
      />

      <Panel title="Appearance">
        <SettingRow
          title="Theme"
          description="Choose light or dark. This is saved on this browser only."
        >
          <SegmentedControl
            aria-label="Theme"
            value={theme}
            onChange={(next) => {
              applyTheme(next);
              setTheme(next);
            }}
            options={[
              { value: "light", label: "Light", icon: "sun", "data-testid": "theme-light" },
              { value: "dark", label: "Dark", icon: "moon", "data-testid": "theme-dark" },
            ]}
            data-testid="settings-theme"
          />
        </SettingRow>
        <SettingRow
          title="Text size"
          description="Changes the size of all text. The default follows your browser."
        >
          <SegmentedControl
            aria-label="Text size"
            value={textSize}
            onChange={(next) => {
              applyTextSize(next);
              setTextSize(next);
            }}
            options={[
              { value: "default", label: "Default", "data-testid": "text-size-default" },
              { value: "large", label: "Large", "data-testid": "text-size-large" },
              { value: "larger", label: "Larger", "data-testid": "text-size-larger" },
            ]}
            data-testid="settings-text-size"
          />
        </SettingRow>
      </Panel>

      <Panel
        title="Dates and times"
        description="Your own choices replace your institution's. Leave them on the default to follow it."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Locale" htmlFor="settings-locale">
            <SelectField
              id="settings-locale"
              value={locale}
              onChange={setLocale}
              options={[
                {
                  value: TENANT_DEFAULT,
                  label: `Institution default (${localeLabel(tenantDefault.locale)})`,
                },
                ...localeOptions(preferences.locale ?? undefined),
              ]}
            />
          </FormField>
          <FormField label="Time zone" htmlFor="settings-time-zone">
            <Combobox
              id="settings-time-zone"
              value={timeZone}
              onChange={setTimeZone}
              options={[
                {
                  value: TENANT_DEFAULT,
                  label: `Institution default (${tenantDefault.timeZone.replaceAll("_", " ")})`,
                },
                ...timeZoneOptions(preferences.timeZone ?? undefined),
              ]}
              icon={null}
            />
          </FormField>
        </div>
        <Text tone="muted" className="mt-4" data-testid="settings-region-preview">
          Reads like {formatDateTime(SAMPLE_INSTANT, { zone: true, region: chosen })}
        </Text>
      </Panel>
      <div>
        <Button
          variant="primary"
          disabled={!changed || pending}
          disabledReason={pending ? "Saving your settings." : "Nothing has changed."}
          onClick={() => {
            onSave({
              locale: locale === TENANT_DEFAULT ? null : locale,
              timeZone: timeZone === TENANT_DEFAULT ? null : timeZone,
              textSize: textSize === "default" ? null : textSize,
            });
          }}
          data-testid="settings-save"
        >
          Save settings
        </Button>
      </div>
    </div>
  );
}
